import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { firebaseReady, googleErrorText, googleIdToken } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4Njc0MDA2N3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", identifier: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const afterAuth = (data, message) => {
    setUser(data);
    toast.success(message);
    navigate(data.role === "admin" ? "/admin" : "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !form.email.trim() && !form.phone.trim())
      return toast.error("Enter an email address or a mobile number");
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { identifier: form.identifier, password: form.password }
          : { name: form.name, email: form.email || null, phone: form.phone, password: form.password };
      const { data } = await api.post(url, body);
      afterAuth(data, mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (!firebaseReady) return toast.error("Google sign-in is not configured yet");
    setBusy(true);
    try {
      const idToken = await googleIdToken();
      const { data } = await api.post("/auth/firebase", { id_token: idToken });
      afterAuth(data, "Welcome back");
    } catch (err) {
      toast.error(err?.code ? googleErrorText(err) : errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 grid gap-12 lg:grid-cols-[1fr_420px] items-start">
      <div className="relative hidden lg:block overflow-hidden rounded-3xl">
        <img src={HERO} alt="Modern home" className="h-[560px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 p-10">
          <h2 className="font-display text-4xl text-white leading-tight">
            Your next home
            <br />
            is one search away.
          </h2>
          <p className="mt-3 max-w-sm text-sm text-white/80">
            Save favourites, track enquiries and manage your listings in one place.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 md:p-8 shadow-panel">
        <h1 className="font-display text-2xl">{mode === "login" ? "Log in to Houses7.com" : "Create your account"}</h1>
        <p className="mt-1.5 text-[13px] text-muted-foreground">Welcome to Houses7.com</p>

        <button
          onClick={googleLogin}
          disabled={busy}
          className="mt-6 w-full h-12 rounded-lg bg-foreground text-background text-sm font-bold hover:brightness-110 transition-all duration-200 disabled:opacity-60"
          data-testid="google-login-btn"
        >
          {busy ? "Please wait…" : "Continue with Google"}
        </button>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          One click — no password needed.
        </p>

        <div className="my-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or use a password <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="auth-form">
          {mode === "register" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Full name</Label>
                <Input value={form.name} onChange={set("name")} required className="h-12 rounded-lg" data-testid="auth-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Mobile number</Label>
                <Input type="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="h-12 rounded-lg" data-testid="auth-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} className="h-12 rounded-lg" data-testid="auth-email-input" />
                <p className="text-[11px] text-muted-foreground">Give a mobile number or an email — either one works to log in.</p>
              </div>
            </>
          )}
          {mode === "login" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Email or mobile number</Label>
              <Input value={form.identifier} onChange={set("identifier")} required className="h-12 rounded-lg" data-testid="auth-identifier-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required minLength={mode === "login" ? undefined : 8} className="h-12 rounded-lg" data-testid="auth-password-input" />
            {mode === "register" && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">Use at least 8 characters.</p>
            )}
          </div>
          <Button type="submit" disabled={busy} className="w-full h-12 rounded-lg text-[15px] font-bold hover:brightness-95" data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
        </div>

        <p className="mt-6 text-[13px] text-muted-foreground">
          {mode === "login" ? "New to Houses7.com? " : "Already have an account? "}
          {mode === "login" ? (
            <Link to="/signup" className="font-bold text-foreground underline" data-testid="auth-mode-toggle">
              Sign up
            </Link>
          ) : (
            <button className="font-bold text-foreground underline" onClick={() => setMode("login")} data-testid="auth-mode-toggle">
              Log in
            </button>
          )}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          <Link to="/" className="underline">Back to home</Link>
        </p>
      </div>
    </div>
  );
}
